"use client";

import { c as _c } from "react/compiler-runtime";
import { ArrowUpDown, ArrowUpRight, LayoutList, Search, Star, Table as TableIcon } from "lucide-react";
import Link from "next/link";
import { useMemo, useState } from "react";
import { FamilyMark, GUTTER, ModelCard } from "@/components/timeline/timeline-parts";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/lib/components/table";
import { formatDate, formatMonth, isoDate, type TimelineModel } from "@/lib/timeline-data";
import { cn } from "@/lib/utils";
interface MonthGroup {
  key: string;
  label: string;
  items: TimelineModel[];
}
interface TimelineListProps {
  models: TimelineModel[];
  latestReleasedAt: string | null;
}
export function TimelineList(t0) {
  const $ = _c(69);
  const {
    models,
    latestReleasedAt
  } = t0;
  const [query, setQuery] = useState("");
  const [showFlagshipOnly, setShowFlagshipOnly] = useState(false);
  const [sortOrder, setSortOrder] = useState("newest");
  const [view, setView] = useState("timeline");
  let t1;
  if ($[0] !== query) {
    t1 = query.trim().toLowerCase();
    $[0] = query;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const normalizedQuery = t1;
  let t2;
  if ($[2] !== models || $[3] !== normalizedQuery || $[4] !== showFlagshipOnly || $[5] !== sortOrder) {
    let t3;
    if ($[7] !== normalizedQuery || $[8] !== showFlagshipOnly) {
      t3 = model => {
        if (showFlagshipOnly && !model.significant) {
          return false;
        }
        if (normalizedQuery) {
          const haystack = `${model.name} ${model.id} ${model.family} ${model.providerName}`.toLowerCase();
          if (!haystack.includes(normalizedQuery)) {
            return false;
          }
        }
        return true;
      };
      $[7] = normalizedQuery;
      $[8] = showFlagshipOnly;
      $[9] = t3;
    } else {
      t3 = $[9];
    }
    const list = models.filter(t3);
    let t4;
    if ($[10] !== sortOrder) {
      t4 = (a, b) => {
        const aTime = a.releasedAt ? new Date(a.releasedAt).getTime() : 0;
        const bTime = b.releasedAt ? new Date(b.releasedAt).getTime() : 0;
        return sortOrder === "newest" ? bTime - aTime : aTime - bTime;
      };
      $[10] = sortOrder;
      $[11] = t4;
    } else {
      t4 = $[11];
    }
    t2 = [...list].sort(t4);
    $[2] = models;
    $[3] = normalizedQuery;
    $[4] = showFlagshipOnly;
    $[5] = sortOrder;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  const filtered = t2;
  let t3;
  if ($[12] !== filtered) {
    const byMonth = new Map();
    for (const model_0 of filtered) {
      const date = model_0.releasedAt ? new Date(model_0.releasedAt) : null;
      const key = date ? `${date.getUTCFullYear()}-${date.getUTCMonth()}` : "undated";
      const label = date ? `${formatMonth(model_0.releasedAt)} ${date.getUTCFullYear()}` : "Date unknown";
      if (!byMonth.has(key)) {
        byMonth.set(key, {
          key,
          label,
          items: []
        });
      }
      byMonth.get(key).items.push(model_0);
    }
    t3 = Array.from(byMonth.values());
    $[12] = filtered;
    $[13] = t3;
  } else {
    t3 = $[13];
  }
  const monthGroups = t3;
  const hasResults = filtered.length > 0;
  let t4;
  let t5;
  if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <span className="sr-only">Search models</span>;
    t5 = <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />;
    $[14] = t4;
    $[15] = t5;
  } else {
    t4 = $[14];
    t5 = $[15];
  }
  let t6;
  if ($[16] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = e => setQuery(e.target.value);
    $[16] = t6;
  } else {
    t6 = $[16];
  }
  let t7;
  if ($[17] !== query) {
    t7 = <label className="relative flex-1 md:max-w-md">{t4}{t5}<input type="search" aria-label="Search models, providers, or model IDs" placeholder={"Search models, providers, IDs\u2026"} className="w-full rounded-full border border-border bg-background py-2 pl-9 pr-3 text-sm shadow-sm outline-none transition-colors placeholder:text-muted-foreground focus:border-primary focus:ring-1 focus:ring-primary" value={query} onChange={t6} /></label>;
    $[17] = query;
    $[18] = t7;
  } else {
    t7 = $[18];
  }
  let t8;
  if ($[19] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = () => setShowFlagshipOnly(_temp);
    $[19] = t8;
  } else {
    t8 = $[19];
  }
  const t9 = showFlagshipOnly ? "border-amber-500/40 bg-amber-500/10 text-amber-600 dark:text-amber-400" : "border-border bg-background text-muted-foreground hover:bg-muted";
  let t10;
  if ($[20] !== t9) {
    t10 = cn("inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-medium transition-colors", t9);
    $[20] = t9;
    $[21] = t10;
  } else {
    t10 = $[21];
  }
  const t11 = showFlagshipOnly ? "fill-amber-400 text-amber-400" : "";
  let t12;
  if ($[22] !== t11) {
    t12 = cn("h-3.5 w-3.5", t11);
    $[22] = t11;
    $[23] = t12;
  } else {
    t12 = $[23];
  }
  let t13;
  if ($[24] !== t12) {
    t13 = <Star className={t12} />;
    $[24] = t12;
    $[25] = t13;
  } else {
    t13 = $[25];
  }
  let t14;
  if ($[26] !== showFlagshipOnly || $[27] !== t10 || $[28] !== t13) {
    t14 = <button type="button" aria-pressed={showFlagshipOnly} onClick={t8} className={t10}>{t13}Flagship</button>;
    $[26] = showFlagshipOnly;
    $[27] = t10;
    $[28] = t13;
    $[29] = t14;
  } else {
    t14 = $[29];
  }
  const t15 = `Sort by ${sortOrder === "newest" ? "oldest" : "newest"} first`;
  let t16;
  if ($[30] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = () => setSortOrder(_temp2);
    $[30] = t16;
  } else {
    t16 = $[30];
  }
  let t17;
  if ($[31] === Symbol.for("react.memo_cache_sentinel")) {
    t17 = <ArrowUpDown className="h-3.5 w-3.5" />;
    $[31] = t17;
  } else {
    t17 = $[31];
  }
  const t18 = sortOrder === "newest" ? "Newest" : "Oldest";
  let t19;
  if ($[32] !== t15 || $[33] !== t18) {
    t19 = <button type="button" aria-label={t15} onClick={t16} className="inline-flex items-center gap-1.5 rounded-full border border-border bg-background px-3 py-1.5 text-xs font-medium text-muted-foreground transition-colors hover:bg-muted">{t17}{t18}</button>;
    $[32] = t15;
    $[33] = t18;
    $[34] = t19;
  } else {
    t19 = $[34];
  }
  const t20 = view === "timeline";
  let t21;
  if ($[35] === Symbol.for("react.memo_cache_sentinel")) {
    t21 = () => setView("timeline");
    $[35] = t21;
  } else {
    t21 = $[35];
  }
  const t22 = view === "timeline" ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground";
  let t23;
  if ($[36] !== t22) {
    t23 = cn("inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-medium transition-colors", t22);
    $[36] = t22;
    $[37] = t23;
  } else {
    t23 = $[37];
  }
  let t24;
  if ($[38] === Symbol.for("react.memo_cache_sentinel")) {
    t24 = <LayoutList className="h-3.5 w-3.5" />;
    $[38] = t24;
  } else {
    t24 = $[38];
  }
  let t25;
  if ($[39] !== t20 || $[40] !== t23) {
    t25 = <button type="button" aria-pressed={t20} onClick={t21} className={t23}>{t24}Timeline</button>;
    $[39] = t20;
    $[40] = t23;
    $[41] = t25;
  } else {
    t25 = $[41];
  }
  const t26 = view === "table";
  let t27;
  if ($[42] === Symbol.for("react.memo_cache_sentinel")) {
    t27 = () => setView("table");
    $[42] = t27;
  } else {
    t27 = $[42];
  }
  const t28 = view === "table" ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground";
  let t29;
  if ($[43] !== t28) {
    t29 = cn("inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-medium transition-colors", t28);
    $[43] = t28;
    $[44] = t29;
  } else {
    t29 = $[44];
  }
  let t30;
  if ($[45] === Symbol.for("react.memo_cache_sentinel")) {
    t30 = <TableIcon className="h-3.5 w-3.5" />;
    $[45] = t30;
  } else {
    t30 = $[45];
  }
  let t31;
  if ($[46] !== t26 || $[47] !== t29) {
    t31 = <button type="button" aria-pressed={t26} onClick={t27} className={t29}>{t30}Table</button>;
    $[46] = t26;
    $[47] = t29;
    $[48] = t31;
  } else {
    t31 = $[48];
  }
  let t32;
  if ($[49] !== t25 || $[50] !== t31) {
    t32 = <div className="inline-flex items-center rounded-full border border-border bg-background p-0.5">{t25}{t31}</div>;
    $[49] = t25;
    $[50] = t31;
    $[51] = t32;
  } else {
    t32 = $[51];
  }
  let t33;
  if ($[52] !== t14 || $[53] !== t19 || $[54] !== t32) {
    t33 = <div className="flex flex-wrap items-center gap-2">{t14}{t19}{t32}</div>;
    $[52] = t14;
    $[53] = t19;
    $[54] = t32;
    $[55] = t33;
  } else {
    t33 = $[55];
  }
  let t34;
  if ($[56] !== t33 || $[57] !== t7) {
    t34 = <div className="sticky top-16 z-30 -mx-4 mb-8 border-b border-border/60 bg-background/80 px-4 py-3 backdrop-blur-md"><div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">{t7}{t33}</div></div>;
    $[56] = t33;
    $[57] = t7;
    $[58] = t34;
  } else {
    t34 = $[58];
  }
  let t35;
  if ($[59] !== filtered || $[60] !== hasResults || $[61] !== latestReleasedAt || $[62] !== monthGroups || $[63] !== query || $[64] !== view) {
    t35 = !hasResults ? <div className="mx-auto max-w-md py-20 text-center"><p className="text-sm text-muted-foreground">No models match{" "}<span className="font-medium text-foreground">“{query}”</span>. Try a different model, provider, or family.</p></div> : view === "timeline" ? <div className="relative mx-auto max-w-4xl"><div aria-hidden={true} className={cn("absolute bottom-0 left-0 top-0 flex justify-center", GUTTER)}><div className="h-full w-px bg-gradient-to-b from-transparent via-border to-transparent" /></div><div className="space-y-8">{monthGroups.map(month => <div key={month.key}><div className="mb-3 flex items-center gap-4 md:gap-5"><div className={cn("flex shrink-0 justify-center", GUTTER)}><span className="h-1.5 w-1.5 rounded-full bg-sky-400/60 ring-4 ring-background" /></div><h2 className="text-[11px] font-semibold uppercase tracking-[0.18em] text-sky-500 dark:text-sky-400">{month.label}</h2></div><div className="space-y-3">{month.items.map(model_1 => <div key={model_1.id} className="flex items-stretch gap-4 md:gap-5"><div className={cn("relative flex shrink-0 justify-center pt-6", GUTTER)}><span className={cn("h-2.5 w-2.5 rounded-full ring-4 ring-background", model_1.significant ? "bg-amber-400" : "bg-muted-foreground/40")} /></div><div className="flex-1"><ModelCard model={model_1} latestReleasedAt={latestReleasedAt} /></div></div>)}</div></div>)}</div></div> : <div className="mx-auto max-w-5xl overflow-hidden rounded-xl border border-border/70"><Table><TableHeader><TableRow className="bg-muted/40"><TableHead>Model</TableHead><TableHead>Provider</TableHead><TableHead>Provider release</TableHead><TableHead>Added to gateway</TableHead><TableHead className="text-right">Details</TableHead></TableRow></TableHeader><TableBody>{filtered.map(_temp3)}</TableBody></Table></div>;
    $[59] = filtered;
    $[60] = hasResults;
    $[61] = latestReleasedAt;
    $[62] = monthGroups;
    $[63] = query;
    $[64] = view;
    $[65] = t35;
  } else {
    t35 = $[65];
  }
  let t36;
  if ($[66] !== t34 || $[67] !== t35) {
    t36 = <div>{t34}{t35}</div>;
    $[66] = t34;
    $[67] = t35;
    $[68] = t36;
  } else {
    t36 = $[68];
  }
  return t36;
}
function _temp3(model_2) {
  return <TableRow key={model_2.id}><TableCell><div className="flex items-center gap-2"><FamilyMark family={model_2.family} /><div className="min-w-0"><div className="font-medium">{model_2.name}</div><div className="font-mono text-[11px] text-muted-foreground">{model_2.id}</div></div></div></TableCell><TableCell className="text-muted-foreground">{model_2.providerName}</TableCell><TableCell>{model_2.releasedAt ? <time dateTime={isoDate(model_2.releasedAt)}>{formatDate(model_2.releasedAt)}</time> : <span className="text-muted-foreground">Unknown</span>}</TableCell><TableCell>{model_2.addedAt ? <time dateTime={isoDate(model_2.addedAt)}>{formatDate(model_2.addedAt)}</time> : <span className="text-muted-foreground">—</span>}</TableCell><TableCell className="text-right"><Link href={`/models/${encodeURIComponent(model_2.id)}`} className="inline-flex items-center gap-1 text-xs font-medium text-primary hover:underline">View<ArrowUpRight className="h-3 w-3" /></Link></TableCell></TableRow>;
}
function _temp2(prev) {
  return prev === "newest" ? "oldest" : "newest";
}
function _temp(v) {
  return !v;
}